import styled from '@emotion/styled';
import { Space, Typography } from 'antd';
import type { FC } from 'react';

interface LegendEntry {
  key: string;
  symbolType?: 'circle' | 'square' | 'line' | 'area';
  color: string;
  fillColor?: string;
  label: string;
  lineIsDashed?: boolean;
  lineWidth?: number;
}

const { Text } = Typography;

export const Legend: FC<{ entries: Array<LegendEntry> }> = ({ entries }) => {
  return (
    <LegendList>
      {entries.map((entry) => (
        <Space key={entry.key}>
          {getSymbol(
            entry.symbolType || 'square',
            entry.color,
            entry.fillColor,
            entry.lineIsDashed,
            entry.lineWidth,
          )}
          <Text>{entry.label}</Text>
        </Space>
      ))}
    </LegendList>
  );
};

const LegendList = styled.div`
    position: absolute;
    top: 10px;
    left: 10px;
    background-color: white;
    padding: 8px 12px;
    border-radius: 4px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.3);
    font-size: 12px;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
`;

/**
 * Returns a styled symbol component based on the specified type.
 *
 * @param type - The type of symbol to render ('circle', 'square', 'line', or 'area')
 * @param color - The primary color used for borders and lines
 * @param fillColor - Optional fill color for the symbol interior (defaults to `color` if not provided)
 * @param lineIsDashed - Optional flag to render the symbol with a dashed stroke
 * @param lineWidth - The width of the stroke/border in pixels (defaults to 2)
 * @returns A React element representing the symbol, or `null` if the type is not recognized
 */
const getSymbol = (
  type: 'circle' | 'square' | 'line' | 'area',
  color: string,
  fillColor?: string,
  lineIsDashed?: boolean,
  lineWidth = 2,
) => {
  switch (type) {
    case 'circle':
      return (
        <CircleSymbol
          fillColor={fillColor || color}
          borderColor={color}
          lineWidth={lineWidth}
          lineIsDashed={lineIsDashed}
        />
      );
    case 'square':
      return (
        <SquareSymbol
          fillColor={fillColor || color}
          borderColor={color}
          lineWidth={lineWidth}
          lineIsDashed={lineIsDashed}
        />
      );
    case 'line':
      return <LineSymbol color={color} lineWidth={lineWidth} lineIsDashed={lineIsDashed} />;
    case 'area':
      return (
        <AreaSymbol
          fillColor={fillColor || color}
          borderColor={color}
          lineWidth={lineWidth}
          lineIsDashed={lineIsDashed}
        />
      );
    default:
      return null;
  }
};

interface SymbolProps {
  fillColor?: string;
  borderColor?: string;
  color?: string;
  lineWidth?: number;
  lineIsDashed?: boolean;
}

const CircleSymbol = styled.div<SymbolProps>`
    width: 12px;
    height: 12px;
    background-color: ${({ fillColor }) => fillColor};
    border-width: ${({ lineWidth }) => lineWidth}px;
    border-color: ${({ borderColor }) => borderColor};
    border-style: ${({ lineIsDashed }) => (lineIsDashed ? 'dashed' : 'solid')};
    border-radius: 50%;
`;

const SquareSymbol = styled.div<SymbolProps>`
    width: 12px;
    height: 12px;
    background-color: ${({ fillColor }) => fillColor};
    border-width: ${({ lineWidth }) => lineWidth}px;
    border-color: ${({ borderColor }) => borderColor};
    border-style: ${({ lineIsDashed }) => (lineIsDashed ? 'dashed' : 'solid')};
`;

const LineSymbol = styled.div<SymbolProps>`
    width: 12px;
    height: ${({ lineWidth }) => lineWidth}px;
    background-color: transparent;
    border-top: ${({ lineWidth, lineIsDashed, color }) =>
      `${lineWidth}px ${lineIsDashed ? 'dashed' : 'solid'} ${color}`};
`;

const AreaSymbol = styled.div<SymbolProps>`
    width: 12px;
    height: 12px;
    background-color: ${({ fillColor }) => fillColor};
    border-right: ${({ lineWidth, lineIsDashed, borderColor }) =>
      `${lineWidth}px ${lineIsDashed ? 'dashed' : 'solid'} ${borderColor}`};
`;
